import React, { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { SupplierTender } from '../../../../types/procurement-types';
import { ProcurementCard } from './ProcurementCardComponent';
import { toast } from 'react-toastify';
import {
  getSupplierTenders,
  requestForProposal,
  requestTenderAccess,
} from '../../../../services/procurementService';
import { RequestAccessModal, RequestProposalModal } from './ModalComponent';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TenderOpportunitiesSort } from '../../../../types/enums';
import { useTranslation } from 'react-i18next';

const TenderOpportunitiesComponent = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || t('topMatches', 'Top Matches');

  const lastItemRef = useRef<HTMLDivElement | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [activeStatus, setActiveStatus] = useState(initialStatus);
  const [activeStatusEnum, setActiveStatusEnum] = useState<string>(
    TenderOpportunitiesSort.BESTMATCH
  );

  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [isProposalModalOpen, setIsProposalModalOpen] = useState(false);
  const [selectedServiceInformationId, setSelectedServiceInformationId] = useState<string | null>(
    null
  );
  const [selectedServiceInformation, setSelectedServiceInformation] = useState<SupplierTender>();

  const [combinedData, setCombinedData] = useState<SupplierTender[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  //   const [shouldScroll, setShouldScroll] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const supplierId = localStorage.getItem('supplierId') || '';

  const statusMap = [
    t('topMatches', 'Top Matches'),
    t('saved', 'Saved'),
    t('approved', 'Approved'),
    t('denied', 'Denied'),
  ];

  const {
    data: procurements = { data: [], pageNumber: 1, pageSize: 12, totalRecords: 0 },
    isLoading,
    refetch,
    isError,
    isFetching,
  } = useQuery({
    queryKey: ['supplierTenders', activeStatusEnum, pageNumber],
    queryFn: async () => {
      if (!supplierId) throw new Error('Supplier ID is missing.');
      try {
        const response = await getSupplierTenders(supplierId, activeStatusEnum, pageNumber);
        return response;
      } catch (error) {
        toast.error(
          t('failedToLoadProcurements', 'Failed to load procurements. Please try again later.')
        );
        throw error;
      }
    },
    enabled: !!supplierId,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  useEffect(() => {
    const statusEnumMap: { [key: string]: string } = {
      [t('topMatches', 'Top Matches')]: TenderOpportunitiesSort.BESTMATCH,
      [t('saved', 'Saved')]: TenderOpportunitiesSort.SAVED,
      [t('approved', 'Approved')]: TenderOpportunitiesSort.APPROVED,
      [t('denied', 'Denied')]: TenderOpportunitiesSort.DENIED,
    };

    setActiveStatusEnum(statusEnumMap[activeStatus]);
  }, [activeStatus]);

  useEffect(() => {
    if (pageNumber === 1) {
      setCombinedData(procurements.data);
    }
  }, [procurements.data, pageNumber]);

  useEffect(() => {
    if (pageNumber > 1 && !isFetching && procurements.data.length > 0) {
      setCombinedData((prev) => [...prev, ...procurements.data]);
      setIsFetchingMore(false);
    }
  }, [procurements.data, pageNumber, isFetching]);

  const handleStatusChange = (status: string) => {
    setActiveStatus(status);
    setSearchParams({ tab: 'Tender Opportunities', status });
    setPageNumber(1);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleRequestAccessOrProposals = (serviceInformationId: string) => {
    const selectedProcurement = procurements.data.find(
      (item) => item.serviceInformationId === serviceInformationId
    );

    setSelectedServiceInformationId(serviceInformationId);
    setSelectedServiceInformation(selectedProcurement);

    if (selectedProcurement?.requestAccess) {
      setIsProposalModalOpen(true);
    } else {
      setIsAccessModalOpen(true);
    }
  };

  const handleAccessModalClose = () => {
    setIsAccessModalOpen(false);
    setSelectedServiceInformationId(null);
    refetch();
  };

  const handleAccessModalConfirm = async () => {
    if (selectedServiceInformationId) {
      try {
        const response = await requestTenderAccess({
          serviceInformationId: selectedServiceInformationId,
          supplierId,
          requestAccess: true,
        });

        if (response.isSuccess) {
          toast.success(t('requestSentSuccessfully', 'Request sent successfully!'));
          refetch(); // Refresh the data after successful action
        } else {
          toast.error(response.errorMessage);
        }
      } catch (error: any) {
        console.error('Failed to request access:', error);

        if (error.status === 404) {
          toast.error(t('supplierNotVerified', 'Supplier has not been verified'));
        } else {
          toast.error(
            t('failedToRequestAccess', 'Failed to request access. Please try again later.')
          );
        }
      }
    } else {
      toast.error(t('noServiceInformationSelected', 'No service information selected.'));
    }

    handleAccessModalClose();
  };

  const handleProposalModalClose = () => {
    setIsProposalModalOpen(false);
    setSelectedServiceInformationId(null);
    setSelectedServiceInformation(undefined);
    refetch();
  };

  const handleProposalModalConfirm = async (tenderAccessId: string) => {
    if (tenderAccessId) {
      try {
        const response = await requestForProposal({ tenderAccessId });

        if (response.isSuccess) {
          toast.success(t('requestSentSuccessfully', 'Request sent successfully!'));
          refetch(); // Refresh the data after successful action
        }
      } catch (error) {
        console.error('Failed to request proposal:', error);
        toast.error(
          t('failedToRequestProposal', 'Failed to request proposal. Please try again later.')
        );
      }
    } else {
      toast.error(t('noTenderAccessIdProvided', 'No tender access ID provided.'));
    }

    handleProposalModalClose();
  };

  const handleSeeMore = () => {
    setIsFetchingMore(true);
    setPageNumber((prev) => prev + 1);
  };

  const isAllFetched =
    combinedData.length >= procurements.totalRecords || procurements.data.length === 0;

  return (
    <div className="min-h-[75vh] rounded-2xl border border-[#E5E5E5] bg-white p-8">
      <div className="relative mb-6">
        <input
          type="text"
          placeholder={t('search', 'Search')}
          value={searchTerm}
          onChange={handleSearchChange}
          className="w-full rounded-lg border border-[#D0D5DD] bg-white px-3.5 py-2.5 pl-12 text-base text-gray-800"
        />
        <Search
          className="absolute left-4 top-1/2 -translate-y-1/2 transform text-gray-500"
          size={20}
        />
      </div>

      <>
        <div className="mb-6 flex space-x-10 border-b border-[#E5E5E5] pb-4">
          {statusMap.map((status) => (
            <button
              key={status}
              className={`rounded-full px-3.5 py-2.5 text-base ${
                activeStatus === status
                  ? 'bg-green-600 font-semibold text-white'
                  : 'bg-none text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => handleStatusChange(status)}
            >
              {status}
            </button>
          ))}
        </div>

        {isLoading && pageNumber === 1 ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <p className="text-center text-lg font-medium">
              {t('loadingProcurements', 'Loading procurements...')}
            </p>
          </div>
        ) : isError ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <p className="text-center text-lg font-medium text-red-500">
              {t('failedToLoadProcurements', 'Failed to load procurements.')}
            </p>
          </div>
        ) : procurements.data.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3 2xl:w-5/6">
              {combinedData.map((procurementItem) => (
                <ProcurementCard
                  key={procurementItem.serviceInformationId}
                  procurement={procurementItem}
                  onRequestAccess={handleRequestAccessOrProposals}
                />
              ))}
            </div>

            {!isAllFetched && (
              <div className="mt-6 flex items-center justify-center">
                <button
                  className="h-[38px] rounded-[10px] bg-[#FFC726] px-4 text-base font-semibold disabled:opacity-50"
                  onClick={handleSeeMore}
                  disabled={isFetchingMore}
                >
                  {isFetchingMore ? 'Loading...' : 'See More'}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex min-h-[40vh] items-center justify-center">
            <div>
              <h3 className="text-center text-2xl font-semibold text-[#161616]">
                {t('noTenderOpportunities', 'No Tender Opportunities')}
              </h3>
              <p className="text-center text-sm font-medium text-[#7F7F7F]">
                {t(
                  'tenderOpportunitiesNotAvailable',
                  'Tender opportunities are not available for this tab'
                )}
              </p>
            </div>
          </div>
        )}
      </>

      <RequestAccessModal
        isOpen={isAccessModalOpen}
        onClose={handleAccessModalClose}
        onConfirm={handleAccessModalConfirm}
      />

      <RequestProposalModal
        isOpen={isProposalModalOpen}
        onClose={handleProposalModalClose}
        onConfirm={handleProposalModalConfirm}
        selectedServiceInformation={selectedServiceInformation as SupplierTender}
      />
    </div>
  );
};
export default TenderOpportunitiesComponent;
